import { PRACTICE_TITLES } from "./practice";

// Which visits are practice consultas, remembered in this browser per
// account (the API has no practice flag yet; see GUIDED-PR.md, "Server
// side"). A practice consulta is recognised by its id once known, or, while
// one is pending (started here, not yet seen in the list), by its title.
// Read and written with the same try/catch convenience pattern as the
// onboarding gates; without storage it lasts for this page load.

type Account = string | null | undefined;
interface Registry {
  ids: string[];
  pendingSince: number | null;
  // Practice notes whose flag was already checked (ensurePracticeFlag).
  flagged: string[];
  // Practice notes whose guided tour was finished or skipped.
  toured: string[];
}
const EMPTY: Registry = {
  ids: [],
  pendingSince: null,
  flagged: [],
  toured: [],
};
const memory = new Map<string, Registry>();
const keyFor = (account: string): string => `guided:practice:v1:${account}`;

const read = (account: Account): Registry | null => {
  if (!account) return null;
  const key = keyFor(account);
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const value = JSON.parse(raw) as Partial<Registry>;
      return {
        ids: Array.isArray(value.ids) ? value.ids.map(String) : [],
        pendingSince:
          typeof value.pendingSince === "number" ? value.pendingSince : null,
        flagged: Array.isArray(value.flagged) ? value.flagged.map(String) : [],
        toured: Array.isArray(value.toured) ? value.toured.map(String) : [],
      };
    }
  } catch {
    // Fall back to this page load.
  }
  return memory.get(key) ?? { ...EMPTY };
};
const write = (account: string, value: Registry): void => {
  const key = keyFor(account);
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A convenience only.
  }
};

export const startPracticeVisit = (
  account: Account,
  now = Date.now(),
): void => {
  const current = read(account);
  if (!account || !current) return;
  write(account, { ...current, pendingSince: now });
};

export const rememberPracticeVisit = (
  account: Account,
  visitPublicId: string,
): void => {
  const current = read(account);
  if (!account || !current) return;
  const id = visitPublicId.toLowerCase();
  write(account, {
    ...current,
    ids: current.ids.includes(id) ? current.ids : [...current.ids, id],
    pendingSince: null,
  });
};

export const isPracticePending = (account: Account): boolean =>
  read(account)?.pendingSince != null;

export const isPracticeVisitId = (
  account: Account,
  visitPublicId: string | null | undefined,
): boolean =>
  !!visitPublicId && !!read(account)?.ids.includes(visitPublicId.toLowerCase());

// A visit from the list or the visit page: a known practice consulta, or the
// pending one (its practice title), which is then remembered by id.
export const matchPracticeVisit = (
  account: Account,
  visit: { public_id: string; title?: string | null } | null | undefined,
): boolean => {
  if (!visit) return false;
  if (isPracticeVisitId(account, visit.public_id)) return true;
  if (
    isPracticePending(account) &&
    visit.title &&
    PRACTICE_TITLES.has(visit.title.trim())
  ) {
    rememberPracticeVisit(account, visit.public_id);
    return true;
  }
  return false;
};

export const isPracticeFlagChecked = (
  account: Account,
  visitPublicId: string,
): boolean => !!read(account)?.flagged.includes(visitPublicId.toLowerCase());

export const markPracticeFlagChecked = (
  account: Account,
  visitPublicId: string,
): void => {
  const current = read(account);
  if (!account || !current) return;
  const id = visitPublicId.toLowerCase();
  if (current.flagged.includes(id)) return;
  write(account, { ...current, flagged: [...current.flagged, id] });
};

export const isPracticeTourDone = (
  account: Account,
  visitPublicId: string,
): boolean => !!read(account)?.toured.includes(visitPublicId.toLowerCase());

export const setPracticeTourDone = (
  account: Account,
  visitPublicId: string,
  done: boolean,
): void => {
  const current = read(account);
  if (!account || !current) return;
  const id = visitPublicId.toLowerCase();
  const toured = current.toured.filter((entry) => entry !== id);
  write(account, { ...current, toured: done ? [...toured, id] : toured });
};

// Tests only.
export const resetPracticeMemory = (): void => {
  memory.clear();
};
